import { useMemo } from 'react';
import AppTable, {
  ColumnDef,
} from '../../../../../../../../components/common/AppTable';
import {
  formatNumber,
  getLocationNames,
  getLocationRoles,
  getRoleTypeName,
} from '../../../../../../../../utils';
import { ICONS } from '../../../../../../../users/data';
import { SVG } from '../../../../../../../../constants/assetLists';
import {
  DepartmentFormValues,
  OnboardingUserFormValues,
  RoleFormValues,
} from '../../../../types';
import { useOnBoardingContext } from '../../../../../../../../context/onBoarding/OnBoardingContextProvider';
import moment from 'moment';
import { ClientOption } from './NotifyDeptTreeSelect';

type Props = {
  users: OnboardingUserFormValues[];
  roles: RoleFormValues[];
  clients: ClientOption[];
  departments: DepartmentFormValues[];
  selectedKeys: string[];
  onSelectionChange: (keys: string[]) => void;
};

const EmptyComponent = () => (
  <div className="flex justify-center py-10 mt-16">
    <div className="bg-softAqua font-medium px-3 py-1 rounded-lg text-sm">
      עדיין לא הוגדרו משתמשים במערכת
    </div>
  </div>
);

function UsersTable({
  users,
  roles,
  clients,
  departments,
  selectedKeys,
  onSelectionChange,
}: Props) {
  const handleOpenMail = (email: string) =>
    window.open(`mailto:${email}`, '_blank');
  const { allRoleTypes } = useOnBoardingContext();

  const columns = useMemo<ColumnDef<OnboardingUserFormValues>[]>(
    () => [
      {
        title: 'שם משתמש',
        field: 'firstName',
        accessor: (r) => `${r.firstName} ${r.lastName}`,
        sortable: true,
      },
      {
        title: 'אימייל',
        field: 'email',
        accessor: 'email',
        sortable: true,
        render: (r) => (
          <span
            onClick={() => handleOpenMail(r.email)}
            className={`${r.status === 'active' ? 'text-linkBlue underline' : 'text-gray'} cursor-pointer`}
          >
            {r.email || '-'}
          </span>
        ),
      },
      {
        title: 'טלפון',
        field: 'phone',
        accessor: 'phone',
        sortable: true,
        width: 250,
        render: (r) => (
          <span dir="ltr">
            {r.phone
              ? !r.phone.startsWith('972')
                ? formatNumber('972' + r.phone)
                : formatNumber(r.phone)
              : '-'}
          </span>
        ),
      },
      {
        title: 'תפקיד',
        field: 'roleId',
        accessor: (r) => {
          const combinedRoles = getLocationRoles(
            clients,
            r.clientIds,
            r.branchIds,
            r.subBranchIds,
          );
          const role = combinedRoles?.find((rl) => rl.id === r.roleId);
          const roleType = allRoleTypes?.data?.find(
            (rt) => rt._id === role?.roleType,
          );
          return getRoleTypeName(roleType) || '-';
        },
        sortable: true,
        render: (r) => {
          const combinedRoles = getLocationRoles(
            clients,
            r.clientIds,
            r.branchIds,
            r.subBranchIds,
          );
          const role = combinedRoles?.find((rl) => rl.id === r.roleId);
          // const role = roles?.find((rl) => rl.id === r.roleId);
          const roleType = allRoleTypes?.data?.find(
            (rt) => rt._id === role?.roleType,
          );
          const icon = ICONS.find((i) => i.id === role?.icon)?.icon || '';
          return (
            <div className="flex items-center gap-2">
              {icon && (
                <img
                  src={icon}
                  alt="icon"
                  className="w-[20px] h-[20px] object-contain"
                />
              )}
              <span>{getRoleTypeName(roleType) || '-'}</span>
            </div>
          );
        },
      },
      {
        title: 'סטאטוס חשבון',
        field: 'status',
        accessor: 'status',
        sortable: false,
        render: (r) => (
          <div className="flex items-center gap-2">
            {r.status === 'active' ? (
              <div className="w-[8px] h-[8px] rounded-full bg-green" />
            ) : (
              <img
                src={SVG.closeGrayIcon}
                alt="icon"
                className="w-[10px] h-[10px] object-contain"
              />
            )}
            <span>{r.status === 'active' ? 'פעיל' : 'לא פעיל'}</span>
          </div>
        ),
      },
      {
        title: 'משתמש באפליקציה',
        field: 'isAppUser',
        accessor: 'isAppUser',
        sortable: false,
        width: 160,
        render: (r) => (
          <div className="flex justify-center items-center w-full">
            {r.isAppUser ? (
              <img src={SVG.tickGreenIcon} className="w-[25px] h-[25px]" />
            ) : (
              <span style={{ color: r.isAppUser ? '#038D92' : '#707070' }}>
                —
              </span>
            )}
          </div>
        ),
      },
      {
        title: 'בית עסק',
        field: 'clientId',
        accessor: (r) => {
          const locations = getLocationNames(
            clients,
            r.clientIds,
            r.branchIds,
            r.subBranchIds,
            true,
          );
          if (!(locations as string[])?.length) return '-';
          return (locations as string[])?.join(', ') || '-';
        },
        sortable: true,
      },
      {
        title: 'תאריך יצירה',
        field: 'createdAt',
        accessor: (r) => moment(r.createdAt).format('DD/MM/YYYY HH:mm'),
        sortable: true,
        render: (r) => moment(r.createdAt).format('DD/MM/YYYY HH:mm'),
      },
      {
        title: 'משוייך לקבוצה',
        field: 'group',
        accessor: (r) => '-',
        sortable: true,
        render: (r) => '-',
      },
    ],
    [roles, departments, clients, allRoleTypes],
  );

  return (
    <AppTable<OnboardingUserFormValues>
      data={users}
      columns={columns}
      rowKey={(r) => r.id}
      selectable
      selectedKeys={selectedKeys}
      onSelectionChange={onSelectionChange}
      rowDisabled={(r) => r.status !== 'active'}
      showPagination={false}
      totalRecords={users.length}
      currentPage={0}
      onPageChange={() => {}}
      limit={500}
      emptyComponent={<EmptyComponent />}
    />
  );
}

export default UsersTable;
